import { useEffect, useRef, useState, useCallback, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Building2, Loader2, Mail, Shield } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { PasswordStrengthIndicator } from "@/components/shared/PasswordStrengthIndicator";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { useAuth } from "@/contexts/AuthContext";
import { buildEnterpriseUrl } from "@/lib/subdomain";
import {
  acceptEnterpriseInvite,
  verifyEnterpriseInvite,
  acceptEnterpriseInviteWithRegistration,
  enterpriseLogoUrl,
  type VerifyEnterpriseInviteResult,
} from "@/services/platformEnterprisesService";

function roleLabelKey(role: string): string {
  if (role === "enterprise_admin") return "platformEnterprises.inviteRoleAdmin";
  return "platformEnterprises.inviteRoleReviewer";
}

function InviteHeader({
  invite,
  logoFailed,
  onLogoError,
  t,
}: {
  invite: VerifyEnterpriseInviteResult;
  logoFailed: boolean;
  onLogoError: () => void;
  t: (key: string) => string;
}): JSX.Element {
  return (
    <div
      className="flex flex-col items-center gap-3 px-6 pt-8 pb-4"
      style={
        invite.primaryColor
          ? { backgroundColor: `${invite.primaryColor}10` }
          : undefined
      }
    >
      {invite.enterpriseId && !logoFailed ? (
        <img
          src={enterpriseLogoUrl(invite.enterpriseId)}
          alt={invite.enterpriseName}
          className="max-h-12 max-w-[10rem] rounded-lg object-contain"
          onError={onLogoError}
        />
      ) : (
        <div
          className="flex h-14 w-14 items-center justify-center rounded-lg bg-muted"
          style={
            invite.primaryColor
              ? { backgroundColor: `${invite.primaryColor}20`, color: invite.primaryColor }
              : undefined
          }
        >
          <Building2 className="h-7 w-7" />
        </div>
      )}
      <p className="text-sm text-muted-foreground text-center">
        {t("platformEnterprises.inviteTitle")}
      </p>
    </div>
  );
}

function InviteDetails({
  invite,
  t,
}: {
  invite: VerifyEnterpriseInviteResult;
  t: (key: string) => string;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">
            {t("platformEnterprises.inviteEmail")}
          </p>
          <p className="truncate text-sm text-foreground">{invite.email}</p>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Shield className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">
            {t("platformEnterprises.inviteRole")}
          </p>
          <p className="text-sm text-foreground">
            {t(roleLabelKey(invite.role))}
          </p>
        </div>
      </div>
    </div>
  );
}

export function AcceptEnterpriseInvitePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();

  const [verifyStatus, setVerifyStatus] = useState<
    "pending" | "ok" | "invalid" | "expired"
  >("pending");
  const [invite, setInvite] = useState<VerifyEnterpriseInviteResult | null>(null);
  const [accepting, setAccepting] = useState(false);

  const [logoFailed, setLogoFailed] = useState(false);
  const handleLogoError = useCallback(() => setLogoFailed(true), []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setVerifyStatus("invalid");
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;

    verifyEnterpriseInvite(token)
      .then((result) => {
        if (mountedRef.current) {
          setInvite(result);
          setVerifyStatus("ok");
        }
      })
      .catch((err: unknown) => {
        if (!mountedRef.current) return;
        const status =
          err != null &&
          typeof err === "object" &&
          "response" in err &&
          (err as { response?: { status?: number } }).response?.status;
        if (status === 410) {
          setVerifyStatus("expired");
        } else {
          setVerifyStatus("invalid");
        }
      });
  }, [token]);

  const handleAcceptAuthenticated = async (): Promise<void> => {
    if (!token || !invite) return;
    setAccepting(true);
    try {
      await acceptEnterpriseInvite(token);
      window.location.href = buildEnterpriseUrl(invite.subdomain, "/login");
    } catch {
      toast.error(t("platformEnterprises.acceptError"));
    } finally {
      if (mountedRef.current) setAccepting(false);
    }
  };

  const handleRegisterAndAccept = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t("platformEnterprises.passwordTooShort"));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t("platformEnterprises.passwordMismatch"));
      return;
    }
    if (!firstName.trim() || !lastName.trim()) return;

    setAccepting(true);
    try {
      await acceptEnterpriseInviteWithRegistration({
        token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      });
      window.location.href = buildEnterpriseUrl(invite!.subdomain, "/login");
    } catch {
      toast.error(t("platformEnterprises.acceptError"));
    } finally {
      if (mountedRef.current) setAccepting(false);
    }
  };

  if (isLoading || verifyStatus === "pending") {
    return (
      <div className="flex min-h-svh items-center justify-center bg-muted/40">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (verifyStatus === "invalid") {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 bg-muted/40 p-6">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-4 pt-8 pb-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <Building2 className="h-7 w-7 text-destructive" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t("platformEnterprises.invalidToken")}
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/login">{t("platformEnterprises.backToLogin")}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (verifyStatus === "expired") {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 bg-muted/40 p-6">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-4 pt-8 pb-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
              <Building2 className="h-7 w-7 text-amber-600" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t("platformEnterprises.expiredToken")}
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/login">{t("platformEnterprises.backToLogin")}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invite) return <></>;


  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-muted/40 p-6">
      <Card className="w-full max-w-md overflow-hidden">
        <InviteHeader
          invite={invite}
          logoFailed={logoFailed}
          onLogoError={handleLogoError}
          t={t}
        />

        <CardContent className="space-y-6 px-6 pt-6 pb-8">
          <InviteDetails invite={invite} t={t} />

          {isAuthenticated ? (
            <Button
              className="w-full"
              disabled={accepting}
              onClick={() => void handleAcceptAuthenticated()}
            >
              {accepting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {accepting
                ? t("platformEnterprises.accepting")
                : t("platformEnterprises.acceptButton")}
            </Button>
          ) : (
            <form
              onSubmit={(e) => void handleRegisterAndAccept(e)}
              className="space-y-4"
            >
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-foreground">
                  {t("platformEnterprises.inviteFormTitle")}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t("platformEnterprises.inviteFormSubtitle")}
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="invite-firstName">
                    {t("platformEnterprises.fieldFirstName")}
                  </Label>
                  <Input
                    id="invite-firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                    disabled={accepting}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="invite-lastName">
                    {t("platformEnterprises.fieldLastName")}
                  </Label>
                  <Input
                    id="invite-lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    autoComplete="family-name"
                    disabled={accepting}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="invite-password">
                  {t("platformEnterprises.fieldPassword")}
                </Label>
                <PasswordInput
                  id="invite-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                  autoComplete="new-password"
                  disabled={accepting}
                />
                <PasswordStrengthIndicator
                  password={password}
                  showRules
                  rulesLabels={{
                    minLength: t("auth.register.passwordStrength.minLength"),
                    uppercase: t("auth.register.passwordStrength.uppercase"),
                    number: t("auth.register.passwordStrength.number"),
                    special: t("auth.register.passwordStrength.special"),
                  }}
                  strengthLabels={{
                    weak: t("auth.register.passwordStrength.weak"),
                    fair: t("auth.register.passwordStrength.fair"),
                    good: t("auth.register.passwordStrength.good"),
                    strong: t("auth.register.passwordStrength.strong"),
                  }}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="invite-confirmPassword">
                  {t("platformEnterprises.fieldConfirmPassword")}
                </Label>
                <PasswordInput
                  id="invite-confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={8}
                  required
                  autoComplete="new-password"
                  disabled={accepting}
                />
              </div>

              <Button type="submit" className="w-full" disabled={accepting}>
                {accepting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {accepting
                  ? t("platformEnterprises.registering")
                  : t("platformEnterprises.registerAndAccept")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
